import {useEffect, useRef} from 'react'

// PWA OOTB Hooks
import {
    useShopperContextsMutation,
    useUsid,
    useCommerceApi,
    useCustomerId,
    useAccessToken,
    useCustomerType
} from '@salesforce/commerce-sdk-react'

// default hooks
import useMultiSite from './use-multi-site'

// custom hooks
import useShipToCountry from '../hooks/use-ship-to-country'
import useSiteType from '../hooks/use-site-type'

// utils
import {getShipToCountry} from '../utils/ship-to-country-utils'

/**
 * Updates or creates the shopper context after a user logs out, ensuring the context reflects the latest shipping country, locale, and currency.
 * Depending on whether a context needs to be created or updated, this function fetches the current shopper context or creates a new one,
 * then updates or creates the context using the provided API methods.
 *
 * @async
 * @param {Object} params - The parameters for updating or creating the shopper context.
 * @param {string} params.usid - The unique session identifier for the shopper.
 * @param {string} params.customerId - The ID of the customer.
 * @param {Object} params.currentShipToCountry - The current shipping country object, containing at least 'id' and 'currency'.
 * @param {Object} params.locale - The locale object, containing at least 'id'.
 * @param {Object} params.updateShopperContext - The mutation object for updating the shopper context (with a mutateAsync method).
 * @param {Object} params.createShopperContext - The mutation object for creating the shopper context (with a mutateAsync method).
 * @param {Function} params.getTokenWhenReady - Async function that resolves to an access token string.
 * @param {Function} params.getUsidWhenReady - Async function that resolves to a session identifier string.
 * @param {Object} params.api - The API client object, expected to have a 'shopperContexts' property with 'getShopperContext' method.
 * @param {Object} params.site - The site object, containing at least an 'id' property.
 * @param {boolean} params.toCreateContext - Flag indicating whether to create a new shopper context (true) or update an existing one (false).
 * @returns {Promise<void>} Resolves when the shopper context has been updated or created.
 */
async function updateShopperContextAfterLogout({
    customerId,
    currentShipToCountry,
    locale,
    updateShopperContext,
    createShopperContext,
    getTokenWhenReady,
    getUsidWhenReady,
    api,
    site,
    toCreateContext
}) {
    if (!currentShipToCountry) return

    const usid = await getUsidWhenReady()
    const accessToken = await getTokenWhenReady()
    let response = null
    if (!toCreateContext) {
        try {
            response = await api.shopperContexts.getShopperContext({
                parameters: {
                    usid: usid,
                    customerId: customerId
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
        } catch (error) {
            console.log(error)
        }
    }

    const options = {
        parameters: {
            usid: usid,
            siteId: site.id
        },
        body: {
            geoLocation: {
                countryCode: currentShipToCountry?.id
            },
            customQualifiers: {
                currency: currentShipToCountry?.currency,
                locale: locale?.id
            }
        }
    }
    if (response?.customQualifiers) {
        await updateShopperContext.mutateAsync(options)
    } else {
        await createShopperContext.mutateAsync(options)
    }
}

/**
 * Custom hook to manage and update the shopper context based on customer type and shipping country.
 * This hook tracks changes in customer type and shipping country, and updates the shopper context
 * accordingly. It provides a method to update the shopper context after a user logs out.
 *
 * @returns {Object} An object containing:
 * - {Function} updateShopperContextAfterLogout: A function to update the shopper context after logout,
 *   which includes parameters such as user session ID, customer ID, current shipping country, locale,
 *   and other necessary API functions.
 *
 * @example
 * const { updateShopperContextAfterLogout } = useUpdateShopperContext();
 * updateShopperContextAfterLogout();
 */
const useUpdateShopperContext = () => {
    const {shipToCountry} = useShipToCountry()
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const {getUsidWhenReady} = useUsid()
    const {site, locale} = useMultiSite()
    const {isUS} = useSiteType()
    const {customerType} = useCustomerType()
    const api = useCommerceApi()
    const customerId = useCustomerId()
    const {token, getTokenWhenReady} = useAccessToken()

    // Ref to store the previous shipToCountry when the user is logged in
    const previousShipToCountryRef = useRef(null)

    // Ref to track if the initial load is complete
    const initialLoadCompleteRef = useRef(false)
    useEffect(() => {
        if (!initialLoadCompleteRef.current) {
            // Set the initial load complete after the first render
            initialLoadCompleteRef.current = true
            return
        }
        // Only update if the token or customerType has changed, or if the shipToCountry has changed
        if (shipToCountry?.id && previousShipToCountryRef?.current?.id === shipToCountry?.id) return
        previousShipToCountryRef.current = shipToCountry
    }, [token, customerType, shipToCountry])

    // Calculate currentShipToCountry for external use
    const currentShipToCountry = getShipToCountry(previousShipToCountryRef.current?.id, isUS)
    // Return the function and any data you need to use externally
    return {
        updateShopperContextAfterLogout: (toCreateContext) =>
            updateShopperContextAfterLogout({
                customerId,
                currentShipToCountry,
                locale,
                updateShopperContext,
                createShopperContext,
                getTokenWhenReady,
                getUsidWhenReady,
                api,
                site,
                toCreateContext: toCreateContext
            })
    }
}

export default useUpdateShopperContext
